import { AutoComplete, AutoCompleteProps, Input } from 'antd'
import useAxios from 'axios-hooks'
import React, { useEffect, useState } from 'react'
import { webservicesBaseURL } from '../../App'
import { Show } from '../common/Show'
import { getNumbers, hasLetters } from '../common/Utils'
import { format as formatCnpj } from '@fnando/cnpj'
import { TextFieldInput } from '../common/TextField'
import { ReactComponent as VolanteSVG } from '../../assets/icons/volante-icone.svg'
import styled from '@emotion/styled'

type TCfc = {
  id: number
  cnpj: string
  fantasia: string
  municipio: string
}

export const CfcsOrigemAutocomplete = ({
  onChange,
  onSelect,
  filterOption,
  showDescription = false,
  label,
  prefix,
  name,
  selectedCfcValue,
  underAutoComplete,
  ...props
}: {
  onChange?: (value?: any, option?: any) => any
  onSelect?: (value?: any, option?: any) => any
  filterOption?: (value: any, option: any) => any
  showDescription?: boolean
  prefix?: any
  label?: string
  name?: string
  selectedCfcValue?: TCfc | null
  underAutoComplete?: boolean
} & AutoCompleteProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [cnpj, setCnpj] = useState(null) as any
  const [selectedCfc, setSelectedCfc] = useState<TCfc | null>(null)

  const url = webservicesBaseURL + '/contas/cfcs-origem/GO'

  const [{ data: cfcs }] = useAxios(url)

  const handleClick = (e: any) => {
    e?.target?.select?.()
  }

  const handleChangeLocal = (value: any) => {
    value = value ?? ''
    setSelectedCfc(null)
    setSearchValue(value)
    setCnpj(null)
    if (!hasLetters(value)) {
      const localCnpj = getNumbers(value)
      if (localCnpj?.length === 14) setCnpj(localCnpj)
    }
    onChange?.(value.toUpperCase())
  }

  const handleSelectLocal = (value: any, option: any) => {
    setSelectedCfc({
      id: option.cfcid,
      cnpj: option.cfccnpj,
      fantasia: option.cfcfantasia,
      municipio: option.cfcmunicipio,
    })
    onSelect?.(value, { ...option, cfccnpj: formatCnpj(option.cfccnpj) })
  }

  const filterOptionLocal = (value: any, option: any) => {
    const fantasiaMatch = option!.cfcfantasia.toUpperCase().indexOf(value?.toUpperCase()) !== -1
    const cnpjMatch = cnpj === option!.cfccnpj
    return fantasiaMatch || cnpjMatch || !!filterOption?.(value, option)
  }

  useEffect(() => {
    setSelectedCfc(selectedCfcValue ?? null)
  }, [selectedCfcValue])

  return (
    <Container>
      <AutoComplete
        size="large"
        listHeight={300}
        data-cy="cfcOrigem"
        allowClear
        value={props.value ?? searchValue}
        onClick={handleClick}
        onChange={handleChangeLocal}
        onSelect={handleSelectLocal}
        filterOption={filterOptionLocal}
        dataSource={cfcs?.map((c: TCfc) => Cfc({ key: c.id, cfc: c }))}
        showArrow
        {...props}
      >
        <TextFieldInput
          label="CFC Origem"
          size="large"
          style={{ borderRadius: '5px' }}
          prefix={selectedCfc ? <Volante selectedCfc={selectedCfc} /> : null}
          underAutoComplete={underAutoComplete}
        />
      </AutoComplete>
    </Container>
  )
}

const Container = styled.div`
  > div {
    width: 100%;
    .ant-input-affix-wrapper {
      padding: 0 11px !important;
    }
  }
`

const Volante = ({ selectedCfc }: any) => {
  return (
    <Show condition={!!selectedCfc?.fantasia}>
      <VolanteSVG /> &nbsp;
      <strong>{' ' + selectedCfc?.fantasia ? selectedCfc?.fantasia + ' - ' : ''}</strong>
    </Show>
  )
}

const Cfc = ({ key, cfc }: { key: any; cfc: TCfc }) => {
  return (
    <AutoComplete.Option
      key={key}
      value={cfc.cnpj}
      cfcid={cfc.id}
      cfcfantasia={cfc.fantasia.toUpperCase()}
      cfccnpj={getNumbers(cfc.cnpj)}
      cfcmunicipio={cfc.municipio}
    >
      <strong>{cfc.fantasia.toUpperCase()}</strong>
      <br />
      {cfc.cnpj}
      <br />
      <em>{cfc.municipio}</em>
    </AutoComplete.Option>
  )
}

const Description = ({ cfc }: { cfc: TCfc | null }) => {
  if (!cfc) return null

  return (
    <div>
      <div>
        <strong style={{ fontSize: '1.3em' }}>{cfc?.fantasia}</strong>
      </div>
      <div>
        {' '}
        CNPJ: <strong>{formatCnpj(cfc?.cnpj)}</strong>
      </div>
      <div>
        {' '}
        Município:{' '}
        <strong>
          <em>{cfc?.municipio}</em>
        </strong>
      </div>
    </div>
  )
}
